import { isAxiosError } from 'axios';
import { Stack, StackProps } from 'component-library';
import { ReactNode } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { RestApiServerError } from '@/api/rest/resources/errors/RestApiServerError';
import { Logger } from '@/lib/logs/logger';

import { Card } from './o/Card/Card';
import { useWidgetTitle } from './useWidgetTitle';

type WidgetErrorFallbackProps = {
  name: string;
  type: string;
};

const WidgetErrorFallback = ({ name, type, error }: WidgetErrorFallbackProps & { error: FallbackProps['error'] }) => {
  const widgetTitle = useWidgetTitle({ name, type });

  return <ErrorFallback title={widgetTitle} error={error} />;
};

export const ErrorFallback = ({
  title,
  error,
  ...delegated
}: { title?: string; error: FallbackProps['error'] } & StackProps) => {
  const message = useGetErrorMessage(error);

  const cardProps = {
    styles: {
      root: 'rounded-3xl p-4',
    },
  };

  return (
    <Card {...cardProps} {...delegated}>
      <Stack spacing={4}>
        {title && <span className='typography-body1'>{title}</span>}
        <span className='typography-body1 text-text-disabled'>{message}</span>
      </Stack>
    </Card>
  );
};

export const WidgetErrorBoundary = ({ name, type, children }: WidgetErrorFallbackProps & { children: ReactNode }) => {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => <WidgetErrorFallback {...props} name={name} type={type} />}
      onError={(error, info) => {
        const readableError = typeof error === 'string' ? error : JSON.stringify(error);
        const readableInfo = typeof info === 'string' ? info : JSON.stringify(info);
        Logger.error(`Widget rendering error (name: ${name}, type: ${type}): ${readableError} ${readableInfo}`);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export const useGetErrorMessage = (error: FallbackProps['error']): string => {
  const { t } = useTranslation();

  const defaultError = t('global.widget.error.message');

  if (isAxiosError(error)) {
    const { code } = error;

    return code
      ? t(`global.errors.axios.${code}`, {
          defaultValue: defaultError,
        })
      : defaultError;
  }

  // The response interceptor converts server responses into RestApiServerError
  const status = error instanceof RestApiServerError ? error.statusCode : undefined;

  return status
    ? t(`global.errors.http.${status}`, {
        defaultValue: defaultError,
      })
    : defaultError;
};
